import { collectPageActionsFromForm } from '@/common/utils/collectPageActionsFromForm'
import { mergePageDataFieldSnapshot } from '@/common/utils/resolveClientDataActualFields'
import { computed, inject, type InjectionKey } from 'vue'


export interface PageError {
  id: string,
  error: boolean
}
export interface ScrollTo {
  name: string
}
export interface Alerts {
  variable_name: string
  text: string
}

export interface Listener {
  level: string // тип объекта: 'element', 'panel', 'form', 'block'
  id: number // идентификатор объекта формы
  name: string
  event_name: string // имя события
  function: string // функция, которая будет вызываться при этом событии
  web_client_exec: boolean
  to_remove: boolean
}

export interface FontStyleColor {
  r?: number
  g?: number
  b?: number
  hex?: string
}

export interface TextFontStyle {
  italic?: boolean
  bold?: boolean
  name?: string
  size?: number
  color?: FontStyleColor
  isBold?: boolean
  isItalic?: boolean
}

export interface BackgroundColorStyle {
  r?: number
  g?: number
  b?: number
  hex?: string
}
export const CustomPropertiesKeys = {
  SET_ERROR: 'set_error',
  SET_CURSOR_POSITION_TO_END: 'set_cursor_position_to_end',
  REMOVE_TEXT_FIELD_BORDER: 'remove_text_field_border',
  RELOAD_NODE: 'reload_node',
  VERIFY_REGEXP: 'verify_regexp',
  /** Серверный Element.expandAll() — раскрыть все узлы TREETABLE на веб-клиенте */
  EXPAND_ALL: 'expand_all',
  /** Серверный Element.setMultipleLimit(n) — лимит выбора в multiple autocomplete (TEXT) */
  MULTIPLE_LIMIT: 'multiple_limit',
  /** Серверный Element.setAutoComplete(mode) */
  AUTO_COMPLETE: 'auto_complete',
  /** Серверный Element.setEnableSuggestEdit(boolean) */
  ENABLE_SUGGEST_EDIT: 'enable_suggest_edit',
  /** Серверный Element.setMultipleDelimiter(string) */
  MULTIPLE_DELIMITER: 'multiple_delimiter',
  /** Серверный SwingAttachments.appendBytes(name, bytes) — создать временный файл и добавить в ленту */
  APPEND_BYTES: 'append_bytes',
  /** Серверный SwingAttachments.appendFileCopies(UniDataTable) — добавить копии файлов в FileAttachments */
  APPEND_FILE_COPIES: 'append_file_copies',
  /** Серверный Element.setHasEditor(boolean) — строка ввода у MultiSelect и т.п. */
  HAS_EDITOR: 'has_editor',
  /** Серверный Element.openSuggester() — показать autocomplete/suggester (TEXT) */
  OPEN_SUGGESTER: 'open_suggester',
} as const
export type CustomPropertyType = Array<{
  property_name: CustomPropertyName
  property_values?: { type: string, value: any }[]
}>
export type CustomPropertyName = (typeof CustomPropertiesKeys)[keyof typeof CustomPropertiesKeys]
export interface ElementState {
  element_id: number // ид элемента
  element_level: string // уровень элемента (FORM,PAGE,PANEL,ACTION,ELEMENT)
  element_name?: string // имя элемента
  is_visible?: boolean // видимость на странице
  is_editable?: boolean // доступность редактирования
  is_enabled?: boolean // включён/выключен
  is_focused?: boolean // нужно ли на элемент поставить фокус
  is_refill?: boolean // нужно ли перезаполнить данные элемента
  is_empty?: boolean // нужно ли очистить данные элемента
  text_font?: TextFontStyle // стиль шрифта
  background_color?: BackgroundColorStyle // заливка цветом
  remove_text_field_border?: boolean // убрать рамку у текстового поля
  custom_properties?: CustomPropertyType // кастомные свойства элемента (set_error и др.)
}

export interface OpenUrlParameter {
  name: string
  value: string
}

export interface OpenUrl {
  url: string
  parameters?: OpenUrlParameter[]
}

export interface PageData {
  uniData?: {
    fields?: {
      fields?: Record<string, any>
    }
  }
  jsResult?: {
    changed_data?: {
      fields?: {
        fields?: Record<string, any>
      }
    }
    event_listeners?: Listener[] // список event listeners для объектов формы
    states?: ElementState[], // список состояний элементов формы
    open_urls?: OpenUrl,
    page_errors?: PageError[],
    alerts?: Alerts[],
    scroll_to?: ScrollTo[]
    form_id?: string | number
  }
  /** Поля из текущей цепочки (как в GlobalProvider) */
  currentChainData?: {
    fields?: {
      fields?: Record<string, any>
    }
  }
  form?: {
    actions?: {
      action: any[] | any
    }
  }
  formId?: string | number
  $fid?: string | number
  status?: string | number
}

export const PageDataKey: InjectionKey<() => PageData> = Symbol('page-data')
export const PageDataSlotKey: InjectionKey<() => string> = Symbol('page-data-slot')
export const PageRegistryFormIdKey: InjectionKey<() => string> = Symbol('page-registry-form-id')
export const PageWindowInstanceIdKey: InjectionKey<() => string> = Symbol('page-window-instance-id')

/**
 * Компоновка для инъекции данных текущей формы
 */
export function usePageData() {
  const getPageData = inject(PageDataKey, () => ({} as PageData))
  const getPageDataSlot = inject(PageDataSlotKey, () => '')
  const getPageRegistryFormId = inject(PageRegistryFormIdKey, () => '')
  const getPageWindowInstanceId = inject(PageWindowInstanceIdKey, () => '')

  const pageData = computed(() => getPageData())
  const pageDataSlot = computed(() => String(getPageDataSlot() ?? '').trim())
  const pageRegistryFormId = computed(() => String(getPageRegistryFormId() ?? '').trim())
  const pageWindowInstanceId = computed(() => String(getPageWindowInstanceId() ?? '').trim())

  const pageFields = computed(() => mergePageDataFieldSnapshot(pageData.value))

  const pageActions = computed(() => collectPageActionsFromForm(pageData.value.form))

  const pageListeners = computed((): Listener[] => {
    // const listeners = []
    const listeners = pageData.value.jsResult?.event_listeners || []

    // TODO: mn.rumynin
    // listeners.push({
    //   level: 'element',
    //   id: 2081,
    //   name: 'test',
    //   event_name: 'input',
    //   function: 'console.log(`test event`)',
    //   web_client_exec: true,
    //   to_remove: false
    // })
    // listeners.push({
    //   level: 'element',
    //   id: 38552,
    //   name: 'test',
    //   event_name: 'textEdited',
    //   function: 'function () {console.log(`test event textEdited`)}',
    //   web_client_exec: true,
    //   to_remove: false
    // })
    // console.log('listeners', listeners)

    return Array.isArray(listeners) ? listeners : []
  })

  const pageStates = computed((): ElementState[] => {
    const states = pageData.value.jsResult?.states || []
    return Array.isArray(states) ? states : []
  })

  return {
    pageData,
    pageDataSlot,
    pageRegistryFormId,
    pageWindowInstanceId,
    pageFields,
    pageActions,
    pageListeners,
    pageStates,
  }
}
